import { useEffect, useState } from "react";
import api from "@/lib/api";

const stages = ["NEW", "QUALIFYING", "QUALIFIED", "FOLLOW_UP", "WON", "LOST"];

export default function BusinessOperations() {
  const [businesses, setBusinesses] = useState([]);
  const [selected, setSelected] = useState("BIZ001");
  const [detail, setDetail] = useState(null);
  const [leads, setLeads] = useState([]);
  const [inbox, setInbox] = useState(null);
  const [followUps, setFollowUps] = useState([]);
  const [salesOps, setSalesOps] = useState(null);
  const [advertising, setAdvertising] = useState(null);
  const [attribution, setAttribution] = useState([]);
  const [readiness, setReadiness] = useState(null);
  const [providerReadiness, setProviderReadiness] = useState(null);
  const [authority, setAuthority] = useState(null);
  const [onboardingProvider, setOnboardingProvider] = useState("WHATSAPP");
  const [onboardingPlan, setOnboardingPlan] = useState(null);

  const load = async (code = selected) => {
    const [d, l, i, f, s, a, at, r, pr, au] = await Promise.all([
      api.get(`/businesses/${code}`),
      api.get(`/businesses/${code}/leads`),
      api.get(`/businesses/${code}/inbox`),
      api.get(`/businesses/${code}/follow-ups`),
      api.get(`/businesses/${code}/sales-ops`),
      api.get(`/businesses/${code}/advertising`),
      api.get(`/businesses/${code}/attribution/events`),
      api.get(`/businesses/${code}/readiness`),
      api.get(`/businesses/${code}/provider-readiness`),
      api.get(`/businesses/${code}/automation-authority`),
    ]);
    setDetail(d.data); setLeads(l.data); setInbox(i.data); setFollowUps(f.data);
    setSalesOps(s.data); setAdvertising(a.data); setAttribution(at.data);
    setReadiness(r.data); setProviderReadiness(pr.data); setAuthority(au.data);
  };

  useEffect(() => {
    api.get("/businesses").then(r => {
      setBusinesses(r.data || []);
      if (r.data?.[0]?.code) setSelected(r.data[0].code);
    });
  }, []);

  useEffect(() => { if (selected) load(selected); }, [selected]);

  const b = detail?.business;
  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="business-operations">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Multi-Business Operations</p>
          <h1 className="text-2xl font-semibold text-white mt-1">{b?.name || "Business Operations"}</h1>
          <p className="text-sm text-slate-400 mt-1">CRM, inbox, follow-up dan business context dalam satu operating surface.</p>
        </div>
        <select value={selected} onChange={e => setSelected(e.target.value)}
          className="bg-[#0D1321] border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-200">
          {businesses.map(x => <option key={x.code} value={x.code}>{x.name}</option>)}
        </select>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <Card title="Business Context" value={b?.operating_state || "—"} sub={b?.context?.current_focus || "No focus"} />
        <Card title="Inbox" value={inbox?.conversations?.length ?? 0} sub="Conversation records" />
        <Card title="Follow-up" value={followUps.length} sub="Scheduled records" />
      </div>

      <section className="rounded-xl border border-white/10 bg-[#0B101C] p-5">
        <div className="flex items-center justify-between mb-4">
          <div><h2 className="text-white font-medium">Lead Pipeline</h2><p className="text-xs text-slate-500">Real records only — no synthetic KPI.</p></div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-5">
          {stages.map(s => <div key={s} className="rounded-lg bg-white/[0.03] border border-white/[0.06] p-3">
            <div className="text-[10px] text-slate-500">{s.replace("_", " ")}</div>
            <div className="text-xl text-white mt-1">{detail?.lead_pipeline?.[s] ?? 0}</div>
          </div>)}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-slate-500 border-b border-white/10"><tr>
              <th className="py-2">Lead</th><th>Stage</th><th>Source</th><th>Next Action</th><th>Owner</th>
            </tr></thead>
            <tbody>{leads.map(l => <tr key={l.id} className="border-b border-white/[0.05] text-slate-300">
              <td className="py-3">{l.name}</td><td>{l.pipeline_stage}</td><td>{l.source}</td><td>{l.next_action || "—"}</td><td>{l.owner || "—"}</td>
            </tr>)}</tbody>
          </table>
          {!leads.length && <div className="py-8 text-center text-sm text-slate-500">Belum ada lead nyata.</div>}
        </div>
      </section>

      <section className="rounded-xl border border-white/10 bg-[#0B101C] p-5">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <h2 className="text-white font-medium">Automation Readiness</h2>
            <p className="text-xs text-slate-500 mt-1">Internal work may continue while live customer actions remain gated.</p>
          </div>
          <div className="text-xs text-slate-400">
            Internal: {readiness?.ready_for_internal_operations ? "READY" : "BLOCKED"} · Live: {readiness?.ready_for_live_customer_automation ? "READY" : "BLOCKED"}
          </div>
        </div>
        <div className="mt-4 grid lg:grid-cols-2 gap-4">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-600 mb-2">Current blockers</p>
            <div className="flex flex-wrap gap-2">{(readiness?.blockers || []).map(x =>
              <span key={x} className="rounded-md border border-white/10 px-2 py-1 text-xs text-slate-400">{x}</span>
            )}{!readiness?.blockers?.length && <span className="text-xs text-slate-500">None</span>}</div>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-600 mb-2">External authority</p>
            <p className="text-sm text-slate-400">Send reply: {authority?.authority?.SEND_REPLY || "—"} · Ad budget: {authority?.authority?.CHANGE_AD_BUDGET || "—"}</p>
            <p className="text-xs text-slate-600 mt-2">Provider credentials exposed: {providerReadiness?.credential_values_exposed ? "YES" : "NO"}</p>
          </div>
        </div>
      </section>

      <section className="grid lg:grid-cols-3 gap-4">
        <div className="rounded-xl border border-white/10 bg-[#0B101C] p-5">
          <h2 className="text-white font-medium">AI Sales Guardrail</h2>
          <p className="text-xs text-slate-500 mt-1">Qualification + next-action recommendation</p>
          <div className="mt-4 text-sm text-slate-300 space-y-2">
            <p>Knowledge: <span className="text-slate-500">{salesOps?.knowledge_profile?.knowledge_state || "—"}</span></p>
            <p>Commercial commitment: <span className="text-slate-500">{salesOps?.sales_policy?.commercial_commitment_requires_human ? "HUMAN" : "—"}</span></p>
            <p>External auto-send: <span className="text-slate-500">{salesOps?.sales_policy?.external_autosend_enabled ? "ON" : "OFF"}</span></p>
          </div>
        </div>
        <div className="rounded-xl border border-white/10 bg-[#0B101C] p-5">
          <h2 className="text-white font-medium">Advertising Control</h2>
          <p className="text-xs text-slate-500 mt-1">{(advertising?.platforms || []).join(" · ") || "No platform registry"}</p>
          <div className="mt-4 text-sm text-slate-300 space-y-2">
            <p>Campaign mutation: <span className="text-slate-500">{advertising?.policy?.campaign_mutation_enabled ? "ON" : "OFF"}</span></p>
            <p>Budget mutation: <span className="text-slate-500">{advertising?.policy?.budget_mutation_enabled ? "ON" : "OFF"}</span></p>
            <p>Credentials: <span className="text-slate-500">{advertising?.policy?.credentials_connected ? "CONNECTED" : "NOT CONNECTED"}</span></p>
          </div>
        </div>
        <div className="rounded-xl border border-white/10 bg-[#0B101C] p-5">
          <h2 className="text-white font-medium">Attribution</h2>
          <p className="text-2xl text-white mt-3">{attribution.length}</p>
          <p className="text-xs text-slate-500 mt-1">Recorded business events</p>
          <p className="text-xs text-slate-600 mt-4">No synthetic ROAS or revenue is generated.</p>
        </div>
      </section>

      <section className="rounded-xl border border-white/10 bg-[#0B101C] p-5" data-testid="channel-onboarding">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <h2 className="text-white font-medium">Channel Onboarding</h2>
            <p className="text-xs text-slate-500 mt-1">Reusable setup path for messaging, social and marketplace providers.</p>
          </div>
          <div className="flex gap-2">
            <select value={onboardingProvider} onChange={e => { setOnboardingProvider(e.target.value); setOnboardingPlan(null); }}
              className="bg-[#0D1321] border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-200">
              {(providerReadiness?.supported_providers || []).map(p => <option key={p} value={p}>{p.replace("_", " ")}</option>)}
            </select>
            <button onClick={async () => {
              const r = await api.get(`/businesses/${selected}/channel-onboarding/${onboardingProvider}`);
              setOnboardingPlan(r.data);
            }} className="rounded-lg border border-white/10 px-3 py-2 text-sm text-slate-200 hover:bg-white/[0.04]">
              Prepare
            </button>
          </div>
        </div>
        {onboardingPlan && <div className="mt-5">
          <div className="flex flex-wrap gap-2">{onboardingPlan.steps.map((step, idx) =>
            <span key={step} className="rounded-md border border-white/10 px-2 py-1 text-xs text-slate-400">{idx + 1}. {step.replaceAll("_", " ")}</span>
          )}</div>
          <div className="mt-4 grid md:grid-cols-3 gap-3 text-xs">
            <div className="rounded-lg bg-white/[0.03] p-3 text-slate-400">Identity mapping <span className="float-right text-slate-200">REQUIRED</span></div>
            <div className="rounded-lg bg-white/[0.03] p-3 text-slate-400">SKU mapping <span className="float-right text-slate-200">{onboardingPlan.mapping_requirements?.sku ? "REQUIRED" : "N/A"}</span></div>
            <div className="rounded-lg bg-white/[0.03] p-3 text-slate-400">Live enablement <span className="float-right text-slate-200">{onboardingPlan.live_enablement?.enabled ? "ON" : "OFF"}</span></div>
          </div>
          <p className="text-xs text-slate-600 mt-3">Credential values are not accepted here. Provider verification and Founder approval are required before live enablement.</p>
        </div>}
      </section>

      <section className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-white/10 bg-[#0B101C] p-5">
          <h2 className="text-white font-medium">Unified Inbox</h2>
          <p className="text-xs text-slate-500 mt-1">AI draft: {inbox?.policy?.ai_draft_enabled ? "ON" : "OFF"} · Auto-send: {inbox?.policy?.ai_autosend_enabled ? "ON" : "OFF"}</p>
          <div className="mt-4 space-y-2">{(inbox?.conversations || []).slice(0,8).map(c =>
            <div key={c.id} className="rounded-lg bg-white/[0.03] p-3 text-sm text-slate-300">{c.sender_name || c.sender_ref || "Unknown"} <span className="float-right text-xs text-slate-500">{c.status}</span></div>
          )}{!inbox?.conversations?.length && <p className="text-sm text-slate-500">Belum ada conversation.</p>}</div>
        </div>
        <div className="rounded-xl border border-white/10 bg-[#0B101C] p-5">
          <h2 className="text-white font-medium">Channels</h2>
          <div className="mt-4 space-y-2">{(detail?.channels || []).map(c =>
            <div key={c.code} className="flex justify-between rounded-lg bg-white/[0.03] p-3 text-sm"><span className="text-slate-300">{c.label}</span><span className="text-slate-500">{c.connection_state}</span></div>
          )}</div>
          <p className="text-xs text-slate-600 mt-4">External sending remains disabled until channel credentials and approval gates are configured.</p>
        </div>
      </section>
    </div>
  );
}

function Card({ title, value, sub }) {
  return <div className="rounded-xl border border-white/10 bg-[#0B101C] p-4"><p className="text-xs text-slate-500">{title}</p><p className="text-xl text-white mt-1">{value}</p><p className="text-xs text-slate-500 mt-2 line-clamp-2">{sub}</p></div>;
}
